/**
 * Sprint 31 + Sprint 34 · PWA Update-Banner für iOS + Android Standalone-Mode.
 *
 * Vorher: `registerType: 'autoUpdate'` ohne `clientsClaim` → neuer Service-Worker
 * wartete bis ALLE Tabs zu waren. Auf iOS Safari heißt das: PWA komplett
 * deinstallieren + neu installieren um Update zu sehen. Andreas musste das
 * nach jedem Deploy manuell machen.
 *
 * Jetzt:
 * - `registerType: 'prompt'` + Banner mit "JETZT UPDATEN"-Button
 * - Periodischer Check alle 60s ob neuer SW im Cache liegt
 * - Click UPDATE → updateServiceWorker(true) + Hard-Reload-Fallback
 * - "SPÄTER" verbirgt Banner bis nächster Check (User wird nicht genervt)
 *
 * Sprint 34: Hard-Reload-Fallback. Wenn der alte SW (autoUpdate ohne
 * clientsClaim) keinen "waiting"-State produziert, macht
 * `updateServiceWorker(true)` nichts. Fallback: setTimeout 1500ms →
 * window.location.reload(). Garantiert dass der Click immer was tut.
 */
import { useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { APP_VERSION } from '../lib/version';

const UPDATE_CHECK_INTERVAL_MS = 60_000;
const RELOAD_FALLBACK_MS = 1500;

async function handleUpdateClick(
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>,
): Promise<void> {
  try {
    await updateServiceWorker(true);
  } catch {
    // ignored — fallback below greift
  }
  window.setTimeout(() => {
    window.location.reload();
  }, RELOAD_FALLBACK_MS);
}

export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      if (!registration) return;
      window.setInterval(async () => {
        if (!navigator.onLine) return;
        try {
          const resp = await fetch(swUrl, {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' },
          });
          if (resp.ok) {
            await registration.update();
          }
        } catch {
          // Netzwerk-Fehler ignoriert — beim nächsten Tick erneut.
        }
      }, UPDATE_CHECK_INTERVAL_MS);
    },
    onRegisterError(error) {
      // eslint-disable-next-line no-console
      console.warn('[PWA] SW-Registrierung fehlgeschlagen', error);
    },
  });

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return;
      void navigator.serviceWorker?.getRegistration().then((r) => r?.update());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  if (!needRefresh) return null;

  return (
    <div className="pwa-update-banner" role="alert" aria-live="polite">
      <div className="pwa-update-banner__body">
        <RefreshCw size={18} strokeWidth={2.5} aria-hidden />
        <div>
          <strong>Neue Version verfügbar</strong>
          <p>
            Aktuell läuft v{APP_VERSION}. Tippe „Jetzt updaten" — die App
            lädt sich kurz neu mit der neuesten Version.
          </p>
        </div>
      </div>
      <div className="pwa-update-banner__actions">
        <button
          type="button"
          className="pwa-update-banner__btn pwa-update-banner__btn--primary"
          onClick={() => void handleUpdateClick(updateServiceWorker)}
        >
          JETZT UPDATEN
        </button>
        <button
          type="button"
          className="pwa-update-banner__btn"
          onClick={() => setNeedRefresh(false)}
          aria-label="Update später erinnern"
        >
          SPÄTER
        </button>
      </div>
    </div>
  );
}
